export type VisitorMappingSource = 'form' | 'system' | 'query' | 'legacy';

export interface VisitorSystemField {
  name: string;
  key: string;
  legacyKey?: string;
  label: string;
  description: string;
}

export interface VisitorFormField {
  name: string;
  label?: string;
  description?: string;
  type?: string;
  options?: string[];
}

export const SYSTEM_VISITOR_MAPPING_FIELDS: VisitorSystemField[] = [
  { name: 'aimdoc_parent_url', key: 'system.aimdoc_parent_url', legacyKey: 'aimdoc_parent_url', label: 'Source URL', description: 'First URL where the visitor was observed' },
  { name: 'aimdoc_referrer', key: 'system.aimdoc_referrer', legacyKey: 'aimdoc_referrer', label: 'Referrer', description: 'External page that referred the visitor' },
  { name: 'first_seen_at', key: 'system.first_seen_at', label: 'First Seen', description: 'First time the visitor was observed' },
  { name: 'first_conversation_at', key: 'system.first_conversation_at', label: 'First Conversation Date', description: 'First time this visitor started an Aimdoc conversation' },
  { name: 'last_seen_at', key: 'system.last_seen_at', label: 'Last Seen', description: 'Most recent time the visitor was observed' },
  { name: 'last_activity_at', key: 'system.last_activity_at', label: 'Latest Aimdoc Interaction', description: 'Most recent Aimdoc session activity for this visitor' },
  { name: 'visitor_summary', key: 'system.visitor_summary', label: 'Visitor Summary', description: 'AI-generated summary of the visitor profile, conversations, events, and browsing history' },
  { name: 'identification_source', key: 'system.identification_source', label: 'Identification Source', description: '"first_party" when the visitor gave Aimdoc their identity (form, SDK, operator); "enriched" when it came from visitor identification or enrichment — treat enriched as a signal, not a source of truth' },
  { name: 'latest_outcome_name', key: 'system.latest_outcome_name', label: 'Latest Aimdoc Outcome', description: 'System name for the latest conversation outcome assigned by Aimdoc' },
  { name: 'latest_outcome_label', key: 'system.latest_outcome_label', label: 'Latest Aimdoc Outcome Label', description: 'Display label for the latest conversation outcome assigned by Aimdoc' },
  { name: 'latest_outcome_set_at', key: 'system.latest_outcome_set_at', label: 'Latest Aimdoc Outcome Date', description: 'When the latest conversation outcome was assigned' },
  { name: 'first_seen_url', key: 'system.first_seen_url', label: 'First Seen URL', description: 'First observed page URL' },
  { name: 'last_seen_url', key: 'system.last_seen_url', label: 'Last Seen URL', description: 'Most recently observed page URL' },
  { name: 'referrer', key: 'system.referrer', label: 'Referrer URL', description: 'Stored visitor referrer URL' },
  { name: 'ip_address', key: 'system.ip_address', label: 'IP Address', description: 'Visitor IP address' },
  { name: 'hostname', key: 'system.hostname', label: 'Hostname', description: 'Reverse DNS hostname' },
  { name: 'city', key: 'system.city', legacyKey: 'city', label: 'City', description: 'Visitor city' },
  { name: 'region', key: 'system.region', legacyKey: 'region', label: 'Region', description: 'Visitor region/state' },
  { name: 'country', key: 'system.country', legacyKey: 'country', label: 'Country', description: 'Visitor country' },
  { name: 'postal', key: 'system.postal', label: 'Postal Code', description: 'Visitor postal code' },
  { name: 'loc', key: 'system.loc', label: 'Coordinates', description: 'Approximate visitor coordinates' },
  { name: 'org', key: 'system.org', label: 'Network Organization', description: 'Visitor network organization' },
  { name: 'timezone', key: 'system.timezone', label: 'Timezone', description: 'Visitor timezone' },
];

export const formMappingKey = (name: string) => `form.${name}`;
export const queryMappingKey = (name: string) => `query.${name.trim()}`;

export function normalizeQueryParamName(value: string) {
  return value.trim().replace(/^\?+/, '');
}

export function parseVisitorMappingKey(key: string): { source: VisitorMappingSource; name: string } {
  const trimmed = key.trim();
  const dotIndex = trimmed.indexOf('.');
  if (dotIndex > 0) {
    const source = trimmed.slice(0, dotIndex);
    const name = trimmed.slice(dotIndex + 1);
    if ((source === 'form' || source === 'system' || source === 'query') && name) {
      return { source, name };
    }
  }
  return { source: 'legacy', name: trimmed };
}

export function resolveMappingKey(
  mappings: Record<string, string>,
  typedKey: string,
  legacyKey?: string,
) {
  if (Object.prototype.hasOwnProperty.call(mappings, typedKey)) {
    return typedKey;
  }
  if (legacyKey && Object.prototype.hasOwnProperty.call(mappings, legacyKey)) {
    return legacyKey;
  }
  return typedKey;
}

export function getMappingValue(
  mappings: Record<string, string>,
  typedKey: string,
  legacyKey?: string,
) {
  const key = resolveMappingKey(mappings, typedKey, legacyKey);
  return mappings[key];
}

export function buildKnownMappingKeys(formFields: VisitorFormField[]) {
  const known = new Set<string>();
  formFields.forEach((field) => {
    if (!field?.name) return;
    known.add(field.name);
    known.add(formMappingKey(field.name));
  });
  SYSTEM_VISITOR_MAPPING_FIELDS.forEach((field) => {
    known.add(field.key);
    known.add(field.name);
    if (field.legacyKey) known.add(field.legacyKey);
  });
  return known;
}

export function getOrphanedMappingKeys(
  mappings: Record<string, string>,
  formFields: VisitorFormField[],
) {
  const known = buildKnownMappingKeys(formFields);
  return Object.keys(mappings || {}).filter((key) => {
    if (!key) return false;
    const parsed = parseVisitorMappingKey(key);
    if (parsed.source === 'query') return false;
    return !known.has(key);
  });
}

export function getMappedCountForKeys(
  mappings: Record<string, string>,
  keys: Array<{ key: string; legacyKey?: string }>,
) {
  return keys.filter((field) => Boolean(getMappingValue(mappings, field.key, field.legacyKey))).length;
}
